import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  OPPORTUNITIES, COMPANY_EVENTS, MY_OPPORTUNITY_APPLICATIONS,
  APPLY_TO_OPPORTUNITY, REGISTER_FOR_COMPANY_EVENT,
  UPDATE_STUDENT_PROFILE,
} from '../graphql';
import { ME } from '../graphql';
import {
  Briefcase, Calendar, Trophy, Star, Mic, Building2, MapPin,
  Globe, Clock, DollarSign, Users, Search, Filter, CheckCircle,
  ChevronDown, ChevronUp, X, ExternalLink, Zap, GraduationCap,
  SlidersHorizontal, BookOpen,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const EVENT_TYPE_CFG = {
  HACKATHON:   { label: 'Hackathon',   icon: Trophy,   color: 'text-purple-600 bg-purple-50 border-purple-200' },
  COMPETITION: { label: 'Competition', icon: Star,     color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
  EVENT:       { label: 'Event',       icon: Calendar, color: 'text-blue-600   bg-blue-50   border-blue-200' },
  WEBINAR:     { label: 'Webinar',     icon: Mic,      color: 'text-green-600  bg-green-50  border-green-200' },
};

const OPP_STATUS_CFG = {
  PENDING:     { label: 'Applied',     color: 'bg-gray-100  text-gray-700'   },
  SHORTLISTED: { label: 'Shortlisted', color: 'bg-blue-100  text-blue-700'   },
  OFFERED:     { label: 'Offered',  color: 'bg-green-100 text-green-700'  },
  REJECTED:    { label: 'Rejected',    color: 'bg-red-100   text-red-700'    },
  ACCEPTED:    { label: 'Accepted',    color: 'bg-emerald-100 text-emerald-700' },
};

const BRANCHES = ['CSE','ECE','EEE','ME','CE','IT','AIML','DS','Chem','Bio'];

// ── Apply Modal ─────────────────────────────────────────────────────────────
function ApplyModal({ opp, onClose, onApplied }) {
  const [coverNote, setCoverNote] = useState('');
  const [apply, { loading }] = useMutation(APPLY_TO_OPPORTUNITY, {
    refetchQueries: ['Opportunities', 'MyOpportunityApplications'],
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apply({ variables: { opportunityId: opp.id, coverNote: coverNote || null } });
      onApplied();
      onClose();
    } catch (err) { alert(err.message); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="font-bold text-gray-900">Apply — {opp.title}</h2>
            <p className="text-xs text-gray-500">{opp.company?.name}</p>
          </div>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="bg-blue-50 rounded-xl p-3 text-sm text-blue-700">
            <p className="font-medium mb-1">Your profile will be shared with this company:</p>
            <p className="text-xs text-blue-600">Name • College • Branch • Year • Skills • Resume (if uploaded)</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Cover Note (optional)</label>
            <textarea className="input" rows={4} value={coverNote} onChange={e => setCoverNote(e.target.value)}
              placeholder="Why are you a great fit for this role? (max 300 chars)" maxLength={300} />
            <p className="text-xs text-gray-400 mt-0.5 text-right">{coverNote.length}/300</p>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary flex-1">{loading ? 'Applying…' : 'Submit Application'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Opportunity Card ────────────────────────────────────────────────────────
function OppCard({ opp, onApply, appliedStatus }) {
  const [expanded, setExpanded] = useState(false);
  const daysLeft = opp.deadline ? Math.max(0, Math.ceil((new Date(opp.deadline) - new Date()) / 86400000)) : null;
  const urgent = daysLeft !== null && daysLeft <= 3;

  return (
    <div className={`card p-4 transition-shadow hover:shadow-md ${opp.status === 'CLOSED' ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        {opp.company?.logoUrl ? (
          <img src={opp.company.logoUrl} alt={opp.company.name} className="w-12 h-12 rounded-xl object-contain bg-gray-50 border flex-none" onError={e=>{e.target.style.display='none'}} />
        ) : (
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-none" style={{ background: '#1a73e8' }}>
            <Building2 size={20} className="text-white" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${opp.type==='INTERNSHIP' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
              {opp.type === 'INTERNSHIP' ? 'Internship' : 'Full-time'}
            </span>
            {opp.isRemote && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Remote</span>}
            {opp.status === 'CLOSED' && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Closed</span>}
            {urgent && daysLeft > 0 && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{daysLeft}d left</span>}
            {daysLeft === 0 && <span className="text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded-full">Deadline today!</span>}
          </div>
          <h3 className="font-semibold text-gray-900">{opp.title}</h3>
          <p className="text-sm text-gray-500 font-medium">{opp.company?.name}</p>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400 flex-wrap">
            {opp.stipend && <span className="flex items-center gap-0.5"><DollarSign size={11}/>{opp.stipend}</span>}
            {opp.ctc    && <span className="flex items-center gap-0.5"><DollarSign size={11}/>{opp.ctc}</span>}
            {opp.duration && <span className="flex items-center gap-0.5"><Clock size={11}/>{opp.duration}</span>}
            {opp.location && <span className="flex items-center gap-0.5"><MapPin size={11}/>{opp.location}</span>}
            <span className="flex items-center gap-0.5"><Users size={11}/>{opp.openings} opening{opp.openings!==1?'s':''}</span>
          </div>
          {opp.skills?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {opp.skills.slice(0,5).map(s => <span key={s} className="badge text-xs">{s}</span>)}
            </div>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t space-y-2">
          <p className="text-sm text-gray-700">{opp.description}</p>
          <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
            {opp.minCgpa > 0 && <p>Min CGPA: <span className="font-medium text-gray-700">{opp.minCgpa}</span></p>}
            {opp.deadline && <p>Deadline: <span className="font-medium text-gray-700">{opp.deadline}</span></p>}
            {opp.branches?.length > 0 && <p className="col-span-2">Branches: <span className="font-medium text-gray-700">{opp.branches.join(', ')}</span></p>}
          </div>
          {opp.company?.website && (
            <a href={opp.company.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
              <Globe size={11}/> {opp.company.website.replace(/^https?:\/\//, '')}
            </a>
          )}
          {opp.company?.description && <p className="text-xs text-gray-400 italic">{opp.company.description}</p>}
        </div>
      )}

      <div className="flex items-center gap-2 mt-3 pt-3 border-t">
        <button onClick={() => setExpanded(e => !e)} className="btn-secondary text-xs flex items-center gap-1 py-1.5">
          {expanded ? <><ChevronUp size={13}/> Less</> : <><ChevronDown size={13}/> Details</>}
        </button>
        {appliedStatus ? (
          <span className={`ml-auto text-xs px-3 py-1.5 rounded-lg font-medium ${OPP_STATUS_CFG[appliedStatus]?.color || 'bg-gray-100 text-gray-700'}`}>
            {OPP_STATUS_CFG[appliedStatus]?.label || appliedStatus}
          </span>
        ) : opp.status === 'OPEN' ? (
          <button onClick={() => onApply(opp)} className="ml-auto btn-primary text-xs py-1.5 flex items-center gap-1">
            <Zap size={13}/> Apply Now
          </button>
        ) : (
          <span className="ml-auto text-xs text-gray-400">Applications closed</span>
        )}
        <span className="text-xs text-gray-400">{opp.applications} applied</span>
      </div>
    </div>
  );
}

// ── Event Card ──────────────────────────────────────────────────────────────
function EventCard({ ev, onRegister }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = EVENT_TYPE_CFG[ev.eventType] || EVENT_TYPE_CFG.EVENT;
  const Icon = cfg.icon;
  const daysLeft = ev.registrationDeadline ? Math.max(0, Math.ceil((new Date(ev.registrationDeadline) - new Date()) / 86400000)) : null;

  return (
    <div className="card p-4 hover:shadow-md transition-shadow">
      {ev.bannerUrl && (
        <div className="w-full h-28 rounded-lg overflow-hidden mb-3 bg-gray-100">
          <img src={ev.bannerUrl} alt="" className="w-full h-full object-cover" onError={e=>{e.target.parentElement.style.display='none'}} />
        </div>
      )}
      <div className="flex items-start gap-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-none ${cfg.color.split(' ').slice(1,3).join(' ')}`}>
          <Icon size={22} className={cfg.color.split(' ')[0]} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}><Icon size={10}/>{cfg.label}</span>
            {daysLeft !== null && daysLeft <= 5 && daysLeft > 0 && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Reg. closes in {daysLeft}d</span>
            )}
          </div>
          <h3 className="font-semibold text-gray-900">{ev.title}</h3>
          <p className="text-xs text-gray-500 font-medium">{ev.company?.name}</p>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400 flex-wrap">
            <span className="flex items-center gap-0.5"><Calendar size={11}/>{ev.startDate}{ev.endDate && ev.endDate !== ev.startDate ? ` → ${ev.endDate}` : ''}</span>
            {ev.venue && <span className="flex items-center gap-0.5"><MapPin size={11}/>{ev.venue}</span>}
            {ev.prizePool && <span className="flex items-center gap-0.5"><Trophy size={11}/>{ev.prizePool}</span>}
          </div>
          {ev.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {ev.tags.slice(0,4).map(t => <span key={t} className="badge text-xs">{t}</span>)}
            </div>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t text-sm text-gray-600">
          <p>{ev.description}</p>
          {ev.meetingLink && (
            <a href={ev.meetingLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-indigo-600 hover:underline">
              <ExternalLink size={11}/> Join Link
            </a>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 mt-3 pt-3 border-t">
        <button onClick={() => setExpanded(e => !e)} className="btn-secondary text-xs flex items-center gap-1 py-1.5">
          {expanded ? <><ChevronUp size={13}/> Less</> : <><ChevronDown size={13}/> Details</>}
        </button>
        <span className="text-xs text-gray-400 ml-1"><Users size={11} className="inline mr-0.5"/>{ev.registrations} registered</span>
        {ev.isRegistered ? (
          <span className="ml-auto flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 px-3 py-1.5 rounded-lg font-medium">
            <CheckCircle size={13}/> Registered
          </span>
        ) : ev.status === 'OPEN' ? (
          <button onClick={() => onRegister(ev.id)} className="ml-auto btn-primary text-xs py-1.5 flex items-center gap-1">
            <Zap size={13}/> Register
          </button>
        ) : (
          <span className="ml-auto text-xs text-gray-400">Registration closed</span>
        )}
      </div>
    </div>
  );
}

// ── Profile Enhancement Panel ────────────────────────────────────────────────
function ProfileEnhancePanel({ user, onClose }) {
  const [section, setSection] = useState(user?.section || '');
  const [subInput, setSubInput] = useState('');
  const [subjects, setSubjects] = useState(user?.enrolledSubjects || []);
  const [updateProfile, { loading }] = useMutation(UPDATE_STUDENT_PROFILE, { refetchQueries: ['Me'] });

  const addSub = () => {
    const s = subInput.trim();
    if (s && !subjects.includes(s)) setSubjects(prev => [...prev, s]);
    setSubInput('');
  };

  const handleSave = async () => {
    try {
      await updateProfile({ variables: { section: section || null, enrolledSubjects: subjects } });
      onClose();
    } catch (err) { alert(err.message); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="font-bold text-gray-900 flex items-center gap-2"><GraduationCap size={18}/> Enhance Your Profile</h2>
            <p className="text-xs text-gray-500">Help companies & the matcher find you better</p>
          </div>
          <button onClick={onClose}><X size={20} className="text-gray-400"/></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Section / Division</label>
            <input className="input" value={section} onChange={e => setSection(e.target.value)} placeholder="A, B, C or A1, B2…" />
            <p className="text-xs text-gray-400 mt-0.5">Used for matching classmates and finding study partners</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Enrolled Subjects</label>
            <div className="flex gap-2 mb-2">
              <input className="input flex-1" value={subInput} onChange={e => setSubInput(e.target.value)} onKeyDown={e => e.key==='Enter' && (e.preventDefault(), addSub())} placeholder="e.g. Data Structures, DBMS" />
              <button type="button" onClick={addSub} className="btn-secondary px-3">Add</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {subjects.map(s => (
                <span key={s} className="flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 text-xs px-2 py-1 rounded-full">
                  <BookOpen size={10}/>{s}
                  <button onClick={() => setSubjects(prev => prev.filter(x => x !== s))}><X size={10}/></button>
                </span>
              ))}
              {subjects.length === 0 && <p className="text-xs text-gray-400 italic">No subjects added yet</p>}
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button onClick={handleSave} disabled={loading} className="btn-primary flex-1">{loading ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────
export default function OpportunitiesPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('all');
  const [filters, setFilters] = useState({ type: '', remote: false, search: '', branch: '', eventType: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [applyModal, setApplyModal] = useState(null);
  const [showEnhance, setShowEnhance] = useState(false);

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const oppVars = {
    type: filters.type || undefined,
    remote: filters.remote || undefined,
    search: filters.search || undefined,
    branch: filters.branch || undefined,
  };
  const evVars = {
    eventType: filters.eventType || undefined,
    search: filters.search || undefined,
  };

  const { data: oppData } = useQuery(OPPORTUNITIES, { variables: oppVars });
  const { data: evData }  = useQuery(COMPANY_EVENTS, { variables: evVars });
  const { data: myAppsData } = useQuery(MY_OPPORTUNITY_APPLICATIONS);
  const { data: meData }  = useQuery(ME);

  const [registerForEvent] = useMutation(REGISTER_FOR_COMPANY_EVENT, { refetchQueries: ['CompanyEvents'] });

  const opps   = oppData?.opportunities || [];
  const events = evData?.companyEvents  || [];
  const myApps = myAppsData?.myOpportunityApplications || [];
  const me     = meData?.me;

  const appliedMap = {};
  myApps.forEach(a => { appliedMap[a.opportunity.id] = a.status; });

  const filteredOpps = tab === 'applications' ? [] : opps;
  const filteredEvents = tab === 'applications' ? [] : events;

  const hasProfileBoost = me?.section || me?.enrolledSubjects?.length > 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Briefcase className="text-indigo-600" size={26}/> Opportunities
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Internships, jobs, hackathons &amp; webinars from top companies</p>
        </div>
        <button onClick={() => setShowEnhance(true)}
          className={`btn-secondary flex items-center gap-2 text-sm ${hasProfileBoost ? 'border-green-400 text-green-700' : 'border-indigo-300 text-indigo-600'}`}>
          <GraduationCap size={15}/>
          {hasProfileBoost ? '✓ Profile Enhanced' : 'Enhance Profile'}
        </button>
      </div>

      {/* Profile nudge */}
      {!hasProfileBoost && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 flex items-center gap-3">
          <GraduationCap size={20} className="text-indigo-500 flex-none" />
          <div className="flex-1">
            <p className="text-sm font-medium text-indigo-800">Add your section &amp; subjects to get better matches</p>
            <p className="text-xs text-indigo-500">Companies can see which batch & subjects you're enrolled in</p>
          </div>
          <button onClick={() => setShowEnhance(true)} className="text-xs text-indigo-700 font-medium hover:underline flex-none">Set up →</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {[
          { id: 'all',          label: 'All',          count: opps.length + events.length },
          { id: 'internship',   label: 'Internships',  count: opps.filter(o=>o.type==='INTERNSHIP').length },
          { id: 'job',          label: 'Jobs',         count: opps.filter(o=>o.type==='JOB').length },
          { id: 'events',       label: 'Events & Webinars', count: events.length },
          { id: 'applications', label: 'My Applications', count: myApps.length },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab===t.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t.label}
            {t.count > 0 && <span className={`text-xs rounded-full px-1.5 ${tab===t.id ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Search + filters */}
      {tab !== 'applications' && (
        <div className="card p-3 space-y-3">
          <div className="flex gap-2 flex-wrap">
            <div className="flex-1 min-w-48 relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input className="input pl-9 py-2 text-sm" placeholder="Search roles, companies…" value={filters.search} onChange={e => setF('search', e.target.value)} />
            </div>
            <button onClick={() => setShowFilters(f => !f)} className={`btn-secondary flex items-center gap-1.5 text-sm py-2 ${(filters.remote||filters.branch) ? 'border-indigo-400 text-indigo-600' : ''}`}>
              <SlidersHorizontal size={15}/> Filters
            </button>
            {filters.remote && <button onClick={() => setF('remote', false)} className="text-xs text-indigo-600 hover:underline">✕ Remote only</button>}
          </div>
          {showFilters && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2 border-t">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={filters.remote} onChange={e => setF('remote', e.target.checked)} className="rounded" />
                <span className="text-sm text-gray-700">Remote / WFH only</span>
              </label>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Branch</label>
                <select className="input text-sm py-1.5" value={filters.branch} onChange={e => setF('branch', e.target.value)}>
                  <option value="">All Branches</option>
                  {BRANCHES.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              {(tab === 'all' || tab === 'events') && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Event Type</label>
                  <select className="input text-sm py-1.5" value={filters.eventType} onChange={e => setF('eventType', e.target.value)}>
                    <option value="">All Events</option>
                    <option value="HACKATHON">Hackathon</option>
                    <option value="COMPETITION">Competition</option>
                    <option value="EVENT">Event</option>
                    <option value="WEBINAR">Webinar</option>
                  </select>
                </div>
              )}
              <button onClick={() => setFilters({ type:'', remote:false, search:'', branch:'', eventType:'' })}
                className="text-xs text-indigo-600 hover:underline text-left">Clear all</button>
            </div>
          )}
        </div>
      )}

      {/* My Applications Tab */}
      {tab === 'applications' && (
        <div className="space-y-3">
          {myApps.length === 0 ? (
            <div className="card p-12 text-center">
              <Briefcase size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500">No applications yet</p>
              <button onClick={() => setTab('all')} className="btn-primary mt-4 text-sm">Browse Opportunities</button>
            </div>
          ) : myApps.map(a => {
            const o = a.opportunity;
            const sc = OPP_STATUS_CFG[a.status] || OPP_STATUS_CFG.PENDING;
            return (
              <div key={a.id} className="card p-4 flex items-start gap-3">
                {o.company?.logoUrl ? (
                  <img src={o.company.logoUrl} alt="" className="w-12 h-12 rounded-xl object-contain bg-gray-50 border flex-none" />
                ) : (
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-none" style={{ background: '#1a73e8' }}>
                    <Building2 size={18} className="text-white" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">{o.title}</h3>
                  <p className="text-xs text-gray-500">{o.company?.name} • {o.type}</p>
                  {a.coverNote && <p className="text-xs text-gray-400 mt-1 italic line-clamp-1">"{a.coverNote}"</p>}
                  <p className="text-xs text-gray-400 mt-0.5">Applied: {new Date(a.appliedAt).toLocaleDateString('en-IN')}</p>
                </div>
                <span className={`flex-none text-xs px-3 py-1.5 rounded-lg font-medium ${sc.color}`}>{sc.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Opportunities List */}
      {tab !== 'applications' && (
        <div className="space-y-4">
          {/* Opportunities section */}
          {(tab === 'all' || tab === 'internship' || tab === 'job') && (() => {
            const filtered = opps.filter(o => {
              if (tab === 'internship' && o.type !== 'INTERNSHIP') return false;
              if (tab === 'job'        && o.type !== 'JOB')         return false;
              return true;
            });
            return filtered.length === 0 ? null : (
              <div>
                {tab === 'all' && <h2 className="text-sm font-semibold text-gray-700 dark:text-white/70 mb-2">Jobs &amp; Internships</h2>}
                <div className="space-y-3">
                  {filtered.map(o => (
                    <OppCard key={o.id} opp={o} appliedStatus={appliedMap[o.id]} onApply={setApplyModal} />
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Events section */}
          {(tab === 'all' || tab === 'events') && events.length > 0 && (
            <div>
              {tab === 'all' && <h2 className="text-sm font-semibold text-gray-700 dark:text-white/70 mb-2 mt-4">Events &amp; Webinars</h2>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {events.map(ev => (
                  <EventCard key={ev.id} ev={ev}
                    onRegister={(id) => registerForEvent({ variables: { eventId: id } })} />
                ))}
              </div>
            </div>
          )}

          {(tab === 'all' ? opps.length + events.length : tab === 'events' ? events.length : opps.filter(o => o.type === tab.toUpperCase()).length) === 0 && (
            <div className="card p-12 text-center">
              <Briefcase size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500">No opportunities found</p>
              <p className="text-gray-400 text-sm mt-1">Check back soon or try different filters</p>
            </div>
          )}
        </div>
      )}

      {applyModal && <ApplyModal opp={applyModal} onClose={() => setApplyModal(null)} onApplied={() => {}} />}
      {showEnhance && <ProfileEnhancePanel user={me} onClose={() => setShowEnhance(false)} />}
    </div>
  );
}
